<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useDepartmentStore } from '@/stores/department'
import { departmentsApi } from '@/plugins/api'

const departmentStore = useDepartmentStore()

// All departments (for superadmin to see departments they're not member of)
const allDepartments = ref<Array<{ id: string; name: string }>>([])
const loading = ref(false)

// Combined list: user's memberships + all departments for superadmin
const departmentOptions = computed(() => {
  const options: Array<{ id: string; name: string; isMember: boolean }> = []

  // User's memberships first
  for (const membership of departmentStore.availableDepartments) {
    options.push({
      id: membership.departmentId,
      name: membership.departmentName || 'Unknown',
      isMember: true,
    })
  }

  // Add "all departments" option for superadmin at the top
  options.unshift({
    id: '',
    name: 'Все отделы',
    isMember: false,
  })

  // Add other departments that user is not member of
  for (const dept of allDepartments.value) {
    const alreadyIncluded = options.some((o) => o.id === dept.id)
    if (!alreadyIncluded) {
      options.push({
        id: dept.id,
        name: dept.name,
        isMember: false,
      })
    }
  }

  return options
})

// Current selection for v-select
const selectedDepartmentId = computed({
  get: () => departmentStore.currentDepartmentId || '',
  set: (value: string) => {
    departmentStore.switchDepartment(value || undefined)
  },
})

// Current department name for display
const currentDepartmentName = computed(() => {
  if (!selectedDepartmentId.value) return 'Все'
  const option = departmentOptions.value.find(
    (o) => o.id === selectedDepartmentId.value
  )
  return option?.name || 'Неизвестно'
})

// Short name for compact display
const shortName = computed(() => {
  const name = currentDepartmentName.value
  if (name === 'Все') return name
  // Return first 3 chars or abbreviation
  if (name.length <= 5) return name
  // Try to find abbreviation (uppercase letters)
  const abbrev = name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
  if (abbrev.length >= 2 && abbrev.length <= 5) return abbrev
  return name.substring(0, 3)
})

async function loadAllDepartments() {
  loading.value = true
  try {
    const response = await departmentsApi.getAll({ limit: 100 })
    allDepartments.value = response.data?.data || response.data || []
  } catch (error) {
    console.warn('[DepartmentSwitcher] Failed to load departments:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  departmentStore.init()
  loadAllDepartments()
})

// Reload departments when user changes (in case of impersonation)
watch(
  () => departmentStore.availableDepartments,
  () => {
    loadAllDepartments()
  }
)
</script>

<template>
  <v-menu
    :close-on-content-click="false"
    location="bottom end"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        variant="text"
        size="small"
        class="text-none"
      >
        <v-icon
          start
          size="small"
        >
          mdi-domain
        </v-icon>
        {{ shortName }}
        <v-icon
          end
          size="small"
        >
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>

    <v-card
      min-width="250"
      max-width="350"
    >
      <v-card-title class="text-subtitle-2 pb-0">
        Отдел
      </v-card-title>
      <v-card-text class="pt-2">
        <v-select
          v-model="selectedDepartmentId"
          :items="departmentOptions"
          item-title="name"
          item-value="id"
          variant="outlined"
          density="compact"
          hide-details
          :loading="loading"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <v-icon
                  v-if="item.raw.isMember"
                  size="small"
                  color="success"
                >
                  mdi-check-circle
                </v-icon>
                <v-icon
                  v-else-if="item.raw.id"
                  size="small"
                  color="grey"
                >
                  mdi-circle-outline
                </v-icon>
                <v-icon
                  v-else
                  size="small"
                  color="primary"
                >
                  mdi-earth
                </v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>
        <div class="text-caption text-medium-emphasis mt-2">
          Фильтрует задачи и проекты по выбранному отделу
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>
